import type {
  FileLocationRef,
  Group,
  WizardData,
  WizardPhase,
} from "./types";

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export type DuplicateCustomerAssignment = {
  customer_id: string;
  group_ids: string[];
};

export type FileAccountingErrors = {
  files_in_multiple_locations: Array<{
    file_id: string;
    locations: FileLocationRef[];
  }>;
  files_missing_from_all_locations: string[];
};

export type BlockingError =
  | { kind: "no_period" }
  | { kind: "no_files" }
  | { kind: "suggested_group_unresolved"; group_id: string }
  | { kind: "ungrouped_files_remain"; count: number }
  | { kind: "group_missing_customer"; group_id: string }
  | {
      kind: "duplicate_customer_assignment";
      customer_id: string;
      group_ids: string[];
    }
  | { kind: "empty_group_included"; group_id: string }
  | {
      kind: "file_in_multiple_locations";
      file_id: string;
      locations: FileLocationRef[];
    }
  | { kind: "file_missing_from_all_locations"; file_id: string };

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/**
 * Find every location a given file id is referenced from. A coherent
 * wizard state always returns an array of length exactly 1; lengths of
 * 0 or >1 indicate a file-accounting invariant violation.
 */
export function findFileLocations(
  data: WizardData,
  fileId: string,
): FileLocationRef[] {
  const locations: FileLocationRef[] = [];
  if (data.ungrouped_file_ids.includes(fileId)) {
    locations.push({ kind: "ungrouped" });
  }
  for (const group of data.groups) {
    if (group.file_ids.includes(fileId)) {
      locations.push({ kind: "group", group_id: group.id });
    }
  }
  return locations;
}

/**
 * Every selected file id known to the wizard, derived from the canonical
 * `files` map. Order is insertion order of the map.
 */
export function getAllSelectedFileIds(data: WizardData): string[] {
  return Object.keys(data.files);
}

// ---------------------------------------------------------------------------
// Required selectors
// ---------------------------------------------------------------------------

/**
 * Customers that have been assigned to more than one non-empty group.
 * Empty groups and groups with no selected customer are ignored.
 * Returns one entry per duplicated customer, with the colliding group ids
 * in their order of appearance in `data.groups`.
 */
export function getDuplicateCustomerAssignments(
  data: WizardData,
): DuplicateCustomerAssignment[] {
  const customerToGroupIds = new Map<string, string[]>();
  for (const group of data.groups) {
    if (group.file_ids.length === 0) continue;
    if (group.selected_customer_id === null) continue;
    const existing = customerToGroupIds.get(group.selected_customer_id);
    if (existing) {
      existing.push(group.id);
    } else {
      customerToGroupIds.set(group.selected_customer_id, [group.id]);
    }
  }
  const duplicates: DuplicateCustomerAssignment[] = [];
  for (const [customerId, groupIds] of customerToGroupIds.entries()) {
    if (groupIds.length > 1) {
      duplicates.push({ customer_id: customerId, group_ids: groupIds });
    }
  }
  return duplicates;
}

/**
 * File accounting integrity check. Catches:
 *   - any selected file appearing in more than one group/ungrouped location
 *   - any selected file appearing in no location at all
 *
 * A coherent wizard state has both arrays empty.
 */
export function getFileAccountingErrors(
  data: WizardData,
): FileAccountingErrors {
  const filesInMultiple: FileAccountingErrors["files_in_multiple_locations"] =
    [];
  const filesMissing: string[] = [];
  for (const fileId of Object.keys(data.files)) {
    const locations = findFileLocations(data, fileId);
    if (locations.length === 0) {
      filesMissing.push(fileId);
    } else if (locations.length > 1) {
      filesInMultiple.push({ file_id: fileId, locations });
    }
  }
  return {
    files_in_multiple_locations: filesInMultiple,
    files_missing_from_all_locations: filesMissing,
  };
}

/**
 * Non-empty groups with a selected customer, in their existing state
 * order. This is the canonical list of groups that will be uploaded.
 *
 * Empty groups are excluded — and the upload-phase blocking error
 * selector will additionally raise `empty_group_included` if any empty
 * group is sitting in state at upload time, to surface latent bugs.
 */
export function getGroupsReadyForUpload(data: WizardData): Group[] {
  return data.groups.filter(
    (g: Group) => g.file_ids.length > 0 && g.selected_customer_id !== null,
  );
}

// ---------------------------------------------------------------------------
// Phase-specific blocking errors
// ---------------------------------------------------------------------------

function appendFileAccountingErrors(
  errors: BlockingError[],
  data: WizardData,
): void {
  const accounting = getFileAccountingErrors(data);
  for (const entry of accounting.files_in_multiple_locations) {
    errors.push({
      kind: "file_in_multiple_locations",
      file_id: entry.file_id,
      locations: entry.locations,
    });
  }
  for (const fileId of accounting.files_missing_from_all_locations) {
    errors.push({ kind: "file_missing_from_all_locations", file_id: fileId });
  }
}

function appendDuplicateCustomerErrors(
  errors: BlockingError[],
  data: WizardData,
): void {
  for (const duplicate of getDuplicateCustomerAssignments(data)) {
    errors.push({
      kind: "duplicate_customer_assignment",
      customer_id: duplicate.customer_id,
      group_ids: duplicate.group_ids,
    });
  }
}

function appendNonEmptyGroupsMissingCustomer(
  errors: BlockingError[],
  data: WizardData,
): void {
  for (const group of data.groups) {
    if (group.file_ids.length > 0 && group.selected_customer_id === null) {
      errors.push({ kind: "group_missing_customer", group_id: group.id });
    }
  }
}

function appendEmptyGroupsIncluded(
  errors: BlockingError[],
  data: WizardData,
): void {
  for (const group of data.groups) {
    if (group.file_ids.length === 0) {
      errors.push({ kind: "empty_group_included", group_id: group.id });
    }
  }
}

/**
 * All blocking errors that prevent moving forward from the given phase.
 * The UI uses these to disable continue/upload buttons and to surface
 * what is actually wrong.
 *
 * Phase rules:
 *   "select":
 *     - no period chosen
 *     - no files selected (no matcher result yet)
 *   "review_suggested":
 *     - any group still in `suggested` state (not approved or needs_edit)
 *     - file accounting errors
 *   "manual_grouping":
 *     - any ungrouped files remain
 *     - any non-empty group missing a selected customer
 *     - duplicate customer across non-empty groups
 *     - any empty group present in state (operator must dissolve it)
 *     - file accounting errors
 *   "final_review" / "uploading":
 *     - everything from `manual_grouping`
 *     - empty groups present in state (would be sent to upload)
 */
export function getBlockingErrorsForPhase(
  data: WizardData,
  phase: WizardPhase,
): BlockingError[] {
  const errors: BlockingError[] = [];

  switch (phase) {
    case "select": {
      if (data.period_date === "") errors.push({ kind: "no_period" });
      if (Object.keys(data.files).length === 0) {
        errors.push({ kind: "no_files" });
      }
      return errors;
    }

    case "review_suggested": {
      for (const group of data.groups) {
        if (group.state === "suggested") {
          errors.push({
            kind: "suggested_group_unresolved",
            group_id: group.id,
          });
        }
      }
      appendFileAccountingErrors(errors, data);
      return errors;
    }

    case "manual_grouping": {
      if (data.ungrouped_file_ids.length > 0) {
        errors.push({
          kind: "ungrouped_files_remain",
          count: data.ungrouped_file_ids.length,
        });
      }
      appendNonEmptyGroupsMissingCustomer(errors, data);
      appendDuplicateCustomerErrors(errors, data);
      appendEmptyGroupsIncluded(errors, data);
      appendFileAccountingErrors(errors, data);
      return errors;
    }

    case "final_review":
    case "uploading": {
      if (data.ungrouped_file_ids.length > 0) {
        errors.push({
          kind: "ungrouped_files_remain",
          count: data.ungrouped_file_ids.length,
        });
      }
      appendNonEmptyGroupsMissingCustomer(errors, data);
      appendDuplicateCustomerErrors(errors, data);
      appendEmptyGroupsIncluded(errors, data);
      appendFileAccountingErrors(errors, data);
      return errors;
    }

    default: {
      const _exhaustive: never = phase;
      void _exhaustive;
      return errors;
    }
  }
}
